import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { DateRangeParams } from '../types';
import './date-range-selector.css';

interface DateRangeSelectorProps {
  onSearch: (params: DateRangeParams) => void;
  loading: boolean;
}

interface StatusOption {
  label: string;
  value: string;
  className?: string;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ onSearch, loading }) => {
  const [dateRange, setDateRange] = useState<Date[] | null>([]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>('ALL');
  const initialSearchDone = useRef(false);

  // Inicializar con los últimos 30 días
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 31);
    
    setDateRange([thirtyDaysAgo, today]);
  }, []);

  // Opciones de status
  const statusOptions: StatusOption[] = [
    { label: 'Todos', value: 'ALL' },
    { label: 'Pendiente', value: 'PND', className: 'status-PND' },
    { label: 'Guia creada', value: 'SWB', className: 'status-SWB' },
    { label: 'Recolección', value: 'HCA', className: 'status-HCA' },
    { label: 'En transito', value: 'TRN', className: 'status-TRN' },
    { label: 'Entregado', value: 'HDC', className: 'status-HDC' },
    { label: 'Cancelado', value: 'CWB', className: 'status-CWB' },
    { label: 'Devoluciones', value: 'HDR', className: 'status-HDR' },
    { label: 'Excepciones', value: 'EXL', className: 'status-EXL' }
  ];

  // Memoizar la función de búsqueda para evitar recreaciones
  const handleSearch = useCallback(() => {
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      
      // Preparar parámetros de búsqueda
      const searchParams: DateRangeParams = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      // Añadir status solo si está seleccionado
      if (selectedStatus && selectedStatus !== 'ALL') {
        searchParams.status = selectedStatus;
      }
      
      // Enviar parámetros
      onSearch(searchParams);
    }
  }, [dateRange, selectedStatus, onSearch]);

  // Ejecutar búsqueda automáticamente al inicializar el componente
  useEffect(() => {
    if (dateRange && dateRange.length === 2 && !initialSearchDone.current) {
      handleSearch();
      initialSearchDone.current = true;
    }
  }, [dateRange, handleSearch]);

  // Template personalizado para las opciones del dropdown
  const statusOptionTemplate = (option: StatusOption) => {
    return (
      <div className="status-option-item">
        {option.className && <span className={`status-color-square ${option.className}`}></span>}
        <span>{option.label}</span>
      </div>
    );
  };

  // Template para el valor seleccionado
  const selectedStatusTemplate = (option: StatusOption, props: any) => {
    if (option) {
      return statusOptionTemplate(option);
    }
    return <span>{props.placeholder}</span>;
  };

  return (
    <div className="date-range-selector">
      <div className="calendar-container">
        <span className="p-float-label">
          <Calendar 
            value={dateRange} 
            onChange={(e) => setDateRange(e.value as Date[])} 
            selectionMode="range" 
            readOnlyInput
            showIcon
            dateFormat="dd/mm/yy"
            id="dateRange"
          />
        </span>
      </div>
      
      <div className="status-dropdown-container">
        <Dropdown
          value={selectedStatus}
          options={statusOptions}
          onChange={(e) => setSelectedStatus(e.value)}
          optionLabel="label"
          placeholder="Seleccionar estado"
          className="status-dropdown"
          itemTemplate={statusOptionTemplate}
          valueTemplate={selectedStatusTemplate}
        />
      </div>
      
      <Button 
        label="Buscar" 
        icon="pi pi-search" 
        onClick={handleSearch} 
        loading={loading}
      />
    </div>
  );
};

export default DateRangeSelector; 